import { Component } from "react";
import { observer } from "mobx-react";
import { makeObservable, observable, runInAction } from "mobx";

import type { Message, MessageResponse } from "@root/models";
import { PromiseCompletion } from "@classes/PromiseCompletion";
import apiService from "@services/apiService";
import messageService from "@services/messageService";
import { ChatMessage } from "@components/ChatMessage";
import { ImgCalendar } from "@components/Images/ImgCalendar";
import { ImgUser } from "@components/Images/ImgUser";
import { ImgNewChat } from "@components/Images/ImgNewChat";
import { ImgOllamaLogo } from "@components/Images/ImgOllamaLogo";

interface SharedChatProps {
  shareId: string;
}

@observer
class SharedChat extends Component<SharedChatProps> {
  @observable conversation: Message[] | null = null;
  @observable chatTitle: string | null = null;
  @observable createdAt: number | null = null;
  @observable sharedAt: number | null = null;
  @observable createdBy: string | null = null;
  @observable createdByPicture: string | null = null;
  @observable createdByEmailMd5: string | null = null;
  @observable enableSyntaxHighlighting: boolean = true;
  @observable showFullThinking: boolean = false;
  @observable isDataLoaded: boolean = false;
  @observable _copyLoader: PromiseCompletion = new PromiseCompletion();
  private _loader: PromiseCompletion = new PromiseCompletion();

  constructor(props: SharedChatProps) {
    super(props);
    makeObservable(this);
  }
  
  componentDidMount() {    
    document.getElementsByClassName('ui-loader-container')[0]?.remove();
    document.body.classList.add('shared');

    void this._loadChat();
    
    // Check for pending action after login
    const pendingActionStr = sessionStorage.getItem('pendingAction');
    if (pendingActionStr) {
      try {
        const pendingAction = JSON.parse(pendingActionStr);
        if (pendingAction.type === 'useChat' && pendingAction.shareId === this.props.shareId) {
          sessionStorage.removeItem('pendingAction');
          // Execute the action after a short delay to ensure component is ready
          setTimeout(() => {
            void this._handleUseChat();
          }, 100);
        }
      } catch (error) {
        console.error('Failed to parse pending action:', error);
        sessionStorage.removeItem('pendingAction');
      }
    }
  }

  componentWillUnmount(): void {
    document.body.classList.remove('shared');
  }

  private _handleUseChat = async () => {
    const isLoggedIn = !!window.user;

    if (!isLoggedIn) {
      // Store the action in session storage to execute after login
      sessionStorage.setItem('pendingAction', JSON.stringify({
        type: 'useChat',
        shareId: this.props.shareId
      }));
      
      // Redirect to login
      window.location.href = '/auth/login?redirect=' + encodeURIComponent(window.location.pathname + window.location.search);
      return;
    }

    try {
      const response = await apiService.post<{ chatId: string }>('/api/chats/use', {
        shareId: this.props.shareId
      }, { loader: this._copyLoader });

      if (response.chatId) {
        // Redirect to the new chat and clean up URL
        window.location.href = '/';
        // Store chat ID to be loaded after redirect
        sessionStorage.setItem('loadChatId', response.chatId);
      }
    } catch (error) {
      console.error('Failed to copy chat:', error);
      alert('Failed to copy chat. Please try again.');
    }
  };

  render() {
    const conversation = this.conversation;
    const isLoggedIn = !!window.user;

    const chain = conversation ? messageService.getMessageChain(conversation, null) : null;
    const sharedDate = this.sharedAt ? new Date(this.sharedAt).toLocaleDateString() : '~';
    const messages = chain
      ? this._collapseToolCallMessages(chain)
      : null;
    if (messages?.length === 0 && !!conversation?.length) {
      messages.push(...conversation);
    }

    const notFound = !messages && !this._loader.isPending;
    if (notFound && this.isDataLoaded) {
      return (
        <div className="page-container">
          <div className="centered-content">
            <div className="ollama-logo sad">😢</div>
            <h1 className="error-title">404</h1>
            <h2 className="error-subtitle">Chat Not Found</h2>
            <div className="error-description">
              Oops! The chat you're looking for seems to have wandered off. 
              Even our llama couldn't find it!
            </div>
            <button className="home-btn" onClick={() => window.location.href = '/'}>
              Go Home
            </button>
          </div>
        </div>
      );
    }

    return (      
      <div className="share-container">
    
        <header className="share-header">
          <div className="share-header-content">
            <a href="/" className="share-logo">
              <div className="logo-icon"><ImgOllamaLogo width={20} height={23} /></div>
              <span className="logo-text">Ollama Web UI</span>
            </a>
        
            <div className="share-header-actions">
              {messages && (
                <button 
                  className="use-chat-btn" 
                  onClick={this._handleUseChat}
                  disabled={this._copyLoader.isPending}
                >
                  {this._copyLoader.isPending ? 'Copying...' : <><ImgNewChat size={20} />Use this chat</>}
                </button>
              )}
              {!isLoggedIn && <button className="google-login-btn" onClick={() => window.location.href = '/auth/login?redirect=' + encodeURIComponent(window.location.pathname + window.location.search)}>
                <div className="google-icon">G</div>
                Login
              </button>}
            </div>
          </div>
        </header>

        <main className="share-main">
          <div className="share-chat-header">
            
            {messages && <>
              <h1 className="share-chat-title">{this.chatTitle}</h1>
              <div className="share-chat-meta">
                <div className="share-meta-item">
                  <span><ImgCalendar size={16} /></span>&nbsp;
                  <span>Shared on {sharedDate}</span>
                </div>
                <div className="share-meta-item">
                  <span><ImgUser size={16} /></span>&nbsp;
                  <span>{this.createdBy}</span>
                </div>
              </div>
            </>}
            {this._loader.isPending && <div className="loader">Loading...</div>}
          </div>

          <div className="share-messages">
            {messages && messages.map((msg, index) => (
              <ChatMessage
                key={msg.id || index}
                chatId={this.props.shareId}
                currentModel={null}
                isLast={index === messages.length - 1}
                message={msg}
                userName={this.createdBy}
                userPicture={this.createdByPicture}
                userEmailMd5={this.createdByEmailMd5}

                codeHighlightingEnabled={this.enableSyntaxHighlighting}
                showTokenUsage={false}
                showFullThinking={this.showFullThinking}
              />
            ))}
          </div>
        </main>

        <footer className="share-footer">
          <div className="share-footer-content">
          <div className="share-footer-text">
              This chat is publicly shared. Anyone with the link can view this conversation.
          </div>
          </div>
        </footer>
      </div>
    );
  }

  private async _loadChat() {
    try{
      const { messages, title, createdAt, sharedAt, createdBy, createdByPicture, createdByEmailMd5, enableSyntaxHighlighting, showFullThinking } = await apiService.get<MessageResponse>(`/api/chats/${this.props.shareId}/shared`, { loader: this._loader, suppress404: true } );

      runInAction(() => {
          this.conversation = messages;
          this.chatTitle = title;
          this.createdAt = createdAt;
          this.sharedAt = sharedAt ?? null;
          this.createdBy = createdBy;
          this.createdByPicture = createdByPicture;
          this.createdByEmailMd5 = createdByEmailMd5;
          this.enableSyntaxHighlighting = enableSyntaxHighlighting ?? true;
          this.showFullThinking = showFullThinking ?? false;
          this.isDataLoaded = true;
      });
    }
    finally {
      runInAction(() => {
        this.isDataLoaded = true;
      });
    }
  }

  private _collapseToolCallMessages(source: Message[]): Message[] {
    const result = [...source];

    for (let i = 0; i < result.length; i++) {
      const msg = result[i];
      if (msg.role === 'assistant' && (msg.toolsFunctionName || (msg.toolCalls && msg.toolCalls.length > 0))) {
        const collapsed: Message[] = [msg];
        for (let j = i + 1; j < result.length; j++) {
          const next = result[j];
          const shouldStop = next.role === 'user' || j === result.length - 1;
          if (next.role !== 'user' || j === result.length - 1) collapsed.push(next);
          if (shouldStop && collapsed.length > 1) {
            const final = { ...collapsed[collapsed.length - 1] };
            final.id = msg.id;
            final.tokensIn = collapsed.reduce((s, m) => s + (m.tokensIn || 0), 0);
            final.tokensOut = collapsed.reduce((s, m) => s + (m.tokensOut || 0), 0);
            final.timeTaken = collapsed.reduce((s, m) => s + (m.timeTaken || 0), 0);
            final.thinkingTimeTaken = collapsed.reduce((s, m) => s + (m.thinkingTimeTaken || 0), 0);
            final.collapsedMessages = collapsed;
            const reverseCollapsed = [...collapsed].reverse();
            final.modelName = reverseCollapsed.find(m => m.modelName)?.modelName || final.modelName;
            const lastCall = reverseCollapsed.find(m => m.toolsFunctionName || (m.toolCalls && m.toolCalls.length > 0));
            if (lastCall) {
              final.toolsFunctionName = lastCall.toolsFunctionName;
              final.toolsFunctionArguments = lastCall.toolsFunctionArguments;
              final.toolCalls = lastCall.toolCalls;
            }
            const lastTool = reverseCollapsed.find(m => m.role === 'tool');
            if (lastTool) {
              final.errorMessage = lastTool.errorMessage;
              final.isAborted = lastTool.isAborted;
              final.parent = lastTool.parent;
            }

            // Preserve sources from collapsed tool messages so "Sources" block remains visible.
            const allSources = collapsed.filter(m => m.role === 'tool' && m.sources?.length).flatMap(m => m.sources || []);
            const seenLinks = new Set<string>();
            const dedupedSources = allSources.filter(s => {
              if (seenLinks.has(s.link)) return false;
              seenLinks.add(s.link);
              return true;
            });
            if (dedupedSources.length > 0) final.sources = dedupedSources;

            result.splice(i, collapsed.length, final);
            break;
          }
        }
      }
    }

    return result;
  }
}

export default SharedChat;